import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";
const label = "text-[11px] font-bold uppercase tracking-[0.2em] text-white/40";

const toLocalInput = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CreateEvent = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [f, setF] = useState<any>({
    title: "", summary: "", description: "", category: "policy", city: "", venue: "",
    starts_at: "", ends_at: "", cover_image: "", is_paid: false,
  });
  const [tiers, setTiers] = useState<any[]>([{ name: "General", price: 199, quantity: 100, perks: "" }]);
  const [busy, setBusy] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [source, setSource] = useState<any>(isEdit ? null : false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/events/${id}`).then((r) => {
      const ev = r.data;
      setSource(ev);
      setF({
        title: ev.title, summary: ev.summary, description: ev.description, category: ev.category,
        city: ev.city, venue: ev.venue, starts_at: toLocalInput(ev.starts_at),
        ends_at: ev.ends_at ? toLocalInput(ev.ends_at) : "", cover_image: ev.cover_image || "", is_paid: ev.is_paid,
      });
      if (ev.tiers?.length) setTiers(ev.tiers);
    }).catch(() => setSource(false));
  }, [id, isEdit]);

  if (!user)
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="event-auth-gate">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Sign in to {isEdit ? "edit this event" : "create an event"}</h1>
        <button onClick={login} className="mt-8 bg-neon px-8 h-12 text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Join the circle</button>
      </div>
    );

  if (!isEdit && !user.is_organizer && user.role !== "admin")
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="organizer-gate">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Organizer approval required</h1>
        <p className="mt-4 max-w-lg text-sm text-white/50">Only approved organizers can publish events. Apply from your dashboard — admin reviews applications.</p>
        <button onClick={() => navigate("/dashboard")} className="mt-8 border border-white/20 px-8 h-12 text-[12px] font-bold uppercase tracking-[0.2em] hover:border-neon hover:text-neon transition-colors">Go to dashboard</button>
      </div>
    );

  if (isEdit && source === null) return <Loader />;
  if (isEdit && source === false)
    return <div className="mx-auto max-w-[1400px] px-5 py-32 text-white/50" data-testid="event-edit-not-found">Event not found.</div>;
  if (isEdit && source.organizer_id !== user.user_id && user.role !== "admin")
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="event-edit-denied">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Not your event</h1>
        <p className="mt-4 text-sm text-white/50">Only the organizer or an admin can edit this event.</p>
      </div>
    );

  const isAdmin = user.role === "admin";
  const isAdminEditingOthers = isEdit && isAdmin && source.organizer_id !== user.user_id;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        ends_at: f.ends_at || null,
        cover_image: f.cover_image || null,
        starts_at: new Date(f.starts_at).toISOString(),
        tiers: f.is_paid ? tiers.map((t) => ({ ...t, price: Number(t.price), quantity: Number(t.quantity) })) : [],
      };
      if (isEdit) {
        await api.put(`/events/${id}`, payload);
        toast.success(isAdminEditingOthers ? "Saved — live immediately. The organizer has been emailed." : "Event updated.");
        navigate(`/events/${id}`);
      } else {
        const { data } = await api.post("/events", payload);
        toast.success("Event submitted for admin approval.");
        navigate(`/events/${data.event_id}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const notifyAttendees = async () => {
    if (!id) return;
    setNotifyBusy(true);
    try {
      const { data: preview } = await api.post(`/events/${id}/notify-attendees`, null, { params: { dry_run: true } });
      const count = preview.recipients || 0;
      if (!window.confirm(`Notify ${count} attendee${count === 1 ? "" : "s"} about this event's current date, time and venue?`)) {
        return;
      }
      const { data } = await api.post(`/events/${id}/notify-attendees`);
      toast.success(`Notified ${data.notified} attendee${data.notified === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setNotifyBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-16" data-testid="create-event-page">
      <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">{isEdit ? "Edit" : "Organizer"}</span>
      <h1 className="mt-3 font-bebas text-5xl tracking-wide uppercase">{isEdit ? "Edit event" : "Create an event"}</h1>
      {isAdminEditingOthers && (
        <p className="mt-3 text-sm text-pcyellow" data-testid="admin-edit-notice">
          You're editing another organizer's event as admin. Changes go live immediately and the organizer gets notified by email.
        </p>
      )}

      {isEdit && isAdmin && (
        <div className="mt-8 border border-white/15 bg-ink-surface p-6 flex flex-wrap items-center justify-between gap-4" data-testid="notify-attendees-block">
          <div>
            <h3 className="font-bebas text-2xl tracking-wide uppercase">Notify attendees</h3>
            <p className="mt-1 text-sm text-white/50">Rescheduled the date, time or venue? Email everyone who registered or bought a ticket. This is manual — saving the form below does not send anything.</p>
          </div>
          <button
            type="button"
            onClick={notifyAttendees}
            disabled={notifyBusy}
            data-testid="notify-attendees-btn"
            className="h-11 px-6 border border-pcyellow text-pcyellow flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] disabled:opacity-40 hover:bg-pcyellow hover:text-ink-base transition-colors"
          >
            <Send size={14} /> Notify attendees
          </button>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 grid gap-6">
        <div>
          <div className={label}>Title</div>
          <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="event-title-input" className={`mt-2 ${field}`} />
        </div>
        <div>
          <div className={label}>Summary</div>
          <input required value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="event-summary-input" className={`mt-2 ${field}`} />
        </div>
        <div>
          <div className={label}>Description</div>
          <textarea required rows={6} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="event-description-input" className="mt-2 w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          <div>
            <div className={label}>Category</div>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="event-category-select" className={`mt-2 ${field}`}>
              {["policy", "townhall", "workshop", "culture", "sports", "startup"].map((c) => <option key={c} value={c} className="bg-ink-surface">{c}</option>)}
            </select>
          </div>
          <div>
            <div className={label}>City</div>
            <input required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="event-city-input" className={`mt-2 ${field}`} />
          </div>
          <div>
            <div className={label}>Venue</div>
            <input required value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} data-testid="event-venue-input" className={`mt-2 ${field}`} />
          </div>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <div className={label}>Starts at</div>
            <input required type="datetime-local" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} data-testid="event-starts-input" className={`mt-2 ${field}`} />
          </div>
          <div>
            <ImageUpload value={f.cover_image} onChange={(url) => setF({ ...f, cover_image: url })} label="Cover photo (optional)" testid="event-cover-upload" />
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
          <input type="checkbox" checked={f.is_paid} onChange={(e) => setF({ ...f, is_paid: e.target.checked })} data-testid="event-paid-toggle" className="accent-[#FF003C]" />
          This is a paid event (multi-tier ticketing)
        </label>

        {f.is_paid && (
          <div className="border border-white/10 bg-ink-surface p-6" data-testid="tiers-editor">
            <div className={label}>Ticket tiers</div>
            <div className="mt-4 grid gap-4">
              {tiers.map((t, i) => (
                <div key={t.tier_id || i} className="grid gap-3 sm:grid-cols-12 items-center">
                  <input placeholder="Tier name" value={t.name} onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`tier-name-${i}`} className={`sm:col-span-4 ${field}`} />
                  <input type="number" placeholder="₹ Price" value={t.price} onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} data-testid={`tier-price-${i}`} className={`sm:col-span-3 ${field}`} />
                  <input type="number" placeholder="Qty" value={t.quantity} onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} data-testid={`tier-qty-${i}`} className={`sm:col-span-3 ${field}`} />
                  <button type="button" onClick={() => setTiers(tiers.filter((_, j) => j !== i))} data-testid={`tier-remove-${i}`} className="sm:col-span-2 h-11 border border-white/12 grid place-items-center hover:border-neon hover:text-neon transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setTiers([...tiers, { name: "", price: 0, quantity: 50, perks: "" }])} data-testid="add-tier-btn" className="mt-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-neon hover:text-white transition-colors">
              <Plus size={14} /> Add tier
            </button>
          </div>
        )}

        <button disabled={busy} data-testid="event-submit-btn" className="h-12 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] disabled:opacity-40 hover:pc-glow transition-shadow">
          {isEdit ? "Save changes" : "Submit for approval"}
        </button>
      </form>
    </div>
  );
};

export default CreateEvent;
